import React, { useEffect, useMemo, useState } from 'react';
import { Container, Typography, Paper, Box, FormControl, InputLabel, Select, MenuItem, Button, Grid, CircularProgress, Alert } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { format } from 'date-fns';
import { useHomes } from '../context/HomesContext';

const scales = ['30min','1hour','3hours','1day','1week','1month'];

const Temperatures = () => {
  // Use shared homes context
  const { homes, selectedHomeId, setSelectedHomeId, loadHomes: loadHomesContext } = useHomes();
  
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState('');
  const [scale, setScale] = useState('1hour');
  // Only display actual temperature
  const [types] = useState(['temperature']);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load homes on mount if not already loaded
  useEffect(() => {
    if (homes.length === 0) {
      loadHomesContext();
    }
  }, []);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        if (!selectedHomeId) { setRooms([]); setRoomId(''); return; }
        const res = await axios.get(`/api/netatmo/homestatus/${selectedHomeId}`);
        const rooms = res.data?.status?.home?.rooms || [];
        setRooms(rooms);
        if (!roomId && rooms.length) setRoomId(rooms[0].id);
      } catch (e) {
        console.error(e);
        setError('Failed to load homestatus');
      }
    };
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHomeId]);

  // Auto-fetch measures when home, room and scale are all available
  useEffect(() => {
    if (selectedHomeId && roomId && scale && types.length > 0) {
      fetchRoomMeasures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHomeId, roomId, scale]);

  // No toggling; both series are always included

  const fetchRoomMeasures = async () => {
    try {
      setLoading(true);
      setError('');
      setSeries([]);
      if (!selectedHomeId || !roomId || types.length === 0) {
        setError('Please select home, room and at least one type');
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      params.set('home_id', selectedHomeId);
      params.set('room_id', roomId);
  params.set('scale', scale);
  // only temperature requested
  params.append('type', 'temperature');
      params.set('optimize', 'true');
      params.set('real_time', 'false');
      const res = await axios.get(`/api/netatmo/getroommeasure?${params.toString()}`);
      const body = res.data?.body || [];
      // body is array of segments; with only 'temperature' requested, use first segment
      const rows = [];
      (body || []).forEach((seg) => {
        const { beg_time, step_time, value } = seg || {};
        (value || []).forEach((vals, idx) => {
          const ts = (beg_time + idx * step_time) * 1000;
          const v = Array.isArray(vals) ? vals[0] : vals;
          rows.push({ ts, temperature: v });
        });
      });
      rows.sort((a, b) => a.ts - b.ts);
      setSeries(rows);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || 'Failed to load room temperatures');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 3, pb: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
        Temperatures
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="home-select">Home</InputLabel>
            <Select labelId="home-select" label="Home" value={selectedHomeId} onChange={(e) => setSelectedHomeId(e.target.value)}>
              {homes.map(h => (
                <MenuItem key={h.id} value={h.id}>{h.alias || h.name || h.id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="room-select">Room</InputLabel>
                <Select labelId="room-select" label="Room" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
                  {(() => {
                    const home = homes.find(h => h.id === selectedHomeId);
                    const nameById = new Map((home?.rooms || []).map(rr => [rr.id, rr.alias || rr.name]));
                    return rooms.map(r => (
                      <MenuItem key={r.id} value={r.id}>{nameById.get(r.id) || `Room ${r.id}`}</MenuItem>
                    ));
                  })()}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="scale-select">Scale</InputLabel>
                <Select labelId="scale-select" label="Scale" value={scale} onChange={(e) => setScale(e.target.value)}>
                  {scales.map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}><CircularProgress /></Box>
      )}

      {!loading && series.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="ts" type="number" domain={['dataMin','dataMax']} tickFormatter={(v) => format(new Date(v), 'PP')} />
                <YAxis label={{ value: 'Temperature (°C)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} />
                <Tooltip labelFormatter={(v) => format(new Date(v), 'PPpp')} />
                <Line type="monotone" dataKey="temperature" stroke="#e65100" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
          {/* Removed table: chart only */}
        </Paper>
      )}
    </Container>
  );
};

export default Temperatures;
